// Resolve a comparison's column operand to a coarse `OperandLiteralSqlType`
// using the schema-bus snapshot tables in scope for the statement.
//
// Conservative by construction:
//   - bare-name match only (the predicate parser discards the qualifier, so we
//     match the column name across ALL in-scope relations),
//   - ambiguity → 'unknown' (a column found in 2 relations with *different*
//     coarse types must not be flagged — we can't know which the user meant),
//   - relation not in the snapshot (CTE, derived table, un-loaded) → 'unknown'.
// 'unknown' always means "do not flag", so every uncertainty is silent.

import type { OperandLiteralSqlType, OperandType } from '@/features/sql-resolver/types';
import { columnSqlTypeFromDataType } from '@/features/sql-resolver/json/operators/columnSqlType';

import type { ResolveColumnType } from './resolveComparisonTypes';

// Structural minimum (a partial of the schema-bus `TableMeta`). SqlMonaco
// passes `snapshot.tables` straight through.
export type SnapshotColumnLike = { readonly name: string; readonly dataType: string };
export type SnapshotTableLike = {
  readonly name: string;
  readonly schema?: string;
  readonly columns: ReadonlyArray<SnapshotColumnLike>;
};

const fold = (s: string): string => s.toLowerCase();

/**
 * Build a `ResolveColumnType` bound to the relations in scope for one
 * (sub)query. `inScopeRelationNames` are the folded bare names of the FROM /
 * JOIN / DML-target relations; only snapshot tables whose name is in that set
 * contribute candidate columns.
 */
export function makeColumnTypeResolver(
  inScopeRelationNames: ReadonlySet<string>,
  snapshotTables: ReadonlyArray<SnapshotTableLike>,
): ResolveColumnType {
  // Pre-filter to in-scope tables once per (sub)query.
  const scoped = snapshotTables.filter((t) => inScopeRelationNames.has(fold(t.name)));

  return (operand: Extract<OperandType, { kind: 'column' }>): OperandLiteralSqlType => {
    const colFolded = fold(operand.ref.qualified.name);
    const seen = new Set<OperandLiteralSqlType>();
    for (const t of scoped) {
      for (const c of t.columns) {
        if (fold(c.name) !== colFolded) continue;
        const sqlType = columnSqlTypeFromDataType(c.dataType);
        if (sqlType !== 'unknown') seen.add(sqlType);
      }
    }
    // Exactly one confident type → use it. Zero or ambiguous → unknown (skip).
    if (seen.size === 1) return [...seen][0]!;
    return 'unknown';
  };
}
